import { Center, Environment, GizmoHelper, GizmoViewport, Grid, OrbitControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useThree } from "@react-three/fiber";
import Model from "./Model";
import UnifiedModel from "./UnifiedModel";
import LoadStatus from "./LoadStatus";
import { Suspense, useState, useEffect, useRef, useCallback } from "react";
import WorldScaleRef from "./WorldScaleRef";
import UVCheckerPlane from "./UVCheckerPlane";
import { ACESFilmicToneMapping, AgXToneMapping, NeutralToneMapping, CineonToneMapping, LinearToneMapping, ReinhardToneMapping  } from "three";
import Outliner from "./Outliner";
import { Collapse } from "antd";
import InfoPanel from "./InfoPanel";
import DebugPanel from "./DebugPanel";
import { useLocation } from 'react-router-dom';
import { CONFIGURATOR_INSPECTOR } from "../Constants";
import EnvironmentHandler from "./EnvironmentHandler";
import InteractionHintAnimator from "./InteractionHintAnimator";

const toneMappingOptions = {
    "Neutral" : NeutralToneMapping,
    "AGX" : AgXToneMapping,
    "ACES" : ACESFilmicToneMapping,
    "Cineon" : CineonToneMapping, 
    "Linear" : LinearToneMapping,
    "Reinhard" : ReinhardToneMapping,
}

const defaultHdrEnvironmentOptions = {
    "Neutral": "./env/neutral.hdr",
    "Studio": "./env/Studio.hdr",
    "Jewelry": "./env/Jewelry.hdr"
};

const isDirectionalLightHdr = {
    "Neutral": true
}

const defaultPatternOptions = [
  { value: "pattern1", image: "./textures/pattern1.jpg" },
  { value: "pattern2", image: "./textures/pattern2.jpg" },
  { value: "pattern3", image: "./textures/pattern3.jpg" },
];

const Model_Inspector = (props) => {

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    let gizmoQuery = queryParams.get('hide_gizmo');
    let autoRotationQuery = queryParams.get('auto_rotation');
    let autoRotationSpeedQuery = queryParams.get('auto_rotation_speed');
    let environmentHDRQuery = queryParams.get('env');
    let backgroundColorQuery = queryParams.get("background_color");
    let hideGizmo = false;
    let autoRotation = false;
    let autoRotationSpeed = 1;
    let defaultEnvironmentHDR = "Neutral"
    let patternOptions = defaultPatternOptions;
    let hdrEnvironmentOptions = defaultHdrEnvironmentOptions;
    let selectedHDR = environmentHDRQuery ?? props.hdri;
    if (gizmoQuery) {
        hideGizmo = gizmoQuery.toLowerCase() === 'true';
    }
    if (autoRotationQuery) {
        autoRotation = autoRotationQuery.toLowerCase() === 'true';
    }
    if (autoRotationSpeedQuery) {
        autoRotationSpeed = parseFloat(autoRotationSpeedQuery);
    }
    if (props.patternOptions && props.patternOptions?.length > 0) {
        patternOptions =  props.patternOptions;
    }
    if (props.hdrOptions !== null &&
        props.hdrOptions !== undefined &&
        props.hdrOptions !== '' &&
        (!Array.isArray(props.hdrOptions) || props.hdrOptions.length > 0)
    ) {
        hdrEnvironmentOptions = props.hdrOptions;
    }
    if (selectedHDR && (hdrEnvironmentOptions[selectedHDR])) {
        defaultEnvironmentHDR = selectedHDR
    }

    const refModel = './models/RealWorldScaleRef.glb';

    const [loaded, setLoaded] = useState(false);
    const [modelSize, setModelSize] = useState(null);
    const [modelDimensionZ, setModelDimensionZ] = useState(null);
    const [maxSide, setMaxSide] = useState(0);
    const [distance, setDistance] = useState(0.5);
    const [originalDistance, setOriginalDistance] = useState(null);
    const [enableGrid, setEnableGrid] = useState(false);

    const [treeData, setTreeData] = useState(null);
    const [visibleNodes, setVisibleNodes] = useState(undefined);
    const [selectedNode, setSelectedNode] = useState(undefined);
    const [modelMetadata, setModelMetadata] = useState({});

    const [wireframeMode, setWireframeMode] = useState(false);
    const [wireframeOpacity, setWireframeOpacity] = useState(0.5);
    const [wireframeThickness, setWireframeThickness] = useState(1.0);
    const [uvMode, setUVMode] = useState(false);
    const [uvScale, setUVScale] = useState(1.0);
    const [patternsMode, setPatternsMode] = useState(false);
    const [configuratorMode, setConfiguratorMode] = useState(false);
    const [selectedPattern, setSelectedPattern] = useState(patternOptions[0].value);
    const [selectedPatternRotation, setSelectedPatternRotation] = useState(0);
    const [selectedMaterialNode, setSelectedMaterialNode] = useState(null);
    const [materialNodes, setMaterialNodes] = useState(null);
    const [backgroundColor, setBackgroundColor] = useState(
        backgroundColorQuery ? "#" + backgroundColorQuery : "#ffffff"
    );
    const [xRepeat, setXRepeat] = useState(0.1);
    const [yRepeat, setYRepeat] = useState(0.1);
    const [repeatLock, setRepeatLock] = useState(true);
    const [uvSliderVisibility, setUVSliderVisibility] = useState(false);
    const [autoRotate, setAutoRotate] = useState(autoRotation);
    const [toneMapping, setToneMapping] = useState("Neutral");
    const [glToneMapping, setGLToneMapping] = useState(toneMappingOptions["Neutral"])
    const [environmentHDR, setEnvironmentHDR] = useState(defaultEnvironmentHDR);
    const [configuratorOptions, setConfigurationOptions] = useState([]);
    const [interactionHintEnabled, setInteractionHintEnabled] = useState(true);
    const [showInteractionHint , setShowInteractionHint ] = useState(true);
    const [isFocused, setIsFocused] = useState(false);
    const [ariaAnnouncement, setAriaAnnouncement] = useState('');
    const [hintsOpen, setHintsOpen] = useState(false);

    const [nodeClass, setNodeClass] = useState(undefined);
    const [vertexCount, setVertexCount] = useState(undefined);
    const [polygonCount, setPolygonCount] = useState(undefined);
    const [materialName, setMaterialName] = useState(undefined);
    const [materialClass, setMaterialClass] = useState(undefined);
    const [quadsCount, setQuadsCount] = useState(undefined);
    const [triCount, setTriCount] = useState(undefined);
    const [ngonsCount, setNgonsCount] = useState(undefined);
    const [modifiers, _setModifiers] = useState(undefined);
    const [texelDensity, setTexelDensity] = useState(undefined);

    const gridRef = useRef();
    const orbitControlsRef = useRef();
    const viewportFocusRef = useRef(null);
    const originalAutoRotate = useRef(autoRotation);

    const exitKeyboardMode = useCallback(() => {
        setIsFocused(false);
        setHintsOpen(false);
        if (originalAutoRotate.current) setAutoRotate(true);
    }, []);

    const enterKeyboardMode = useCallback(() => {
        setIsFocused(true);
        setHintsOpen(true);
        setAutoRotate(false);
        setInteractionHintEnabled(false);
    }, []);

    const userAgent = typeof window !== 'undefined' ? navigator.userAgent : '';
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    const ariaInstructions = isIOS
        ? "3D model. On touch devices with VoiceOver, double-tap to activate, swipe to orbit; double-tap and hold with one finger until three tones, then drag with a second finger to pan or pinch to zoom."
        : isAndroid
            ? "3D model. To rotate the model, use two fingers and drag to orbit. To pan the camera, use a double-tap and hold gesture, then drag with two fingers. To zoom in or out, pinch with two fingers."
            : "3D model. Use arrow keys to orbit, Shift and arrow keys to pan, Ctrl or Cmd and arrow keys to zoom in and out.";

    const getViewName = useCallback(() => {
        const controls = orbitControlsRef.current;
        if (!controls) return '';
        const azimuth = controls.getAzimuthalAngle();
        const polar = controls.getPolarAngle();
        const TAU = Math.PI * 2;
        const az = ((azimuth % TAU) + TAU) % TAU;
        const sector = Math.floor((az + Math.PI / 4) / (Math.PI / 2)) % 4;
        const horizontal = ['front', 'right', 'back', 'left'][sector];
        let vertical = '';
        if (polar < Math.PI / 4) vertical = 'top ';
        else if (polar > (3 * Math.PI) / 4) vertical = 'bottom ';
        return `${vertical}${horizontal} view`;
    }, []);

    const announceView = useCallback((prefix = '') => {
        const view = getViewName();
        if (!view) return;
        const message = `${prefix}${view.charAt(0).toUpperCase()}${view.slice(1)}`;
        setAriaAnnouncement(message);
    }, [getViewName]);

    const getPaddingStyles = (target) => {
        if (!props.paddings || !Array.isArray(props.paddings)) return {};
        
        const targetPadding = props.paddings.find(p => p.target === target);
        if (!targetPadding) return {};
        
        const styles = {};
        if (targetPadding.paddingTop) styles.paddingTop = `${targetPadding.paddingTop}px`;
        if (targetPadding.paddingBottom) styles.paddingBottom = `${targetPadding.paddingBottom}px`;
        if (targetPadding.paddingLeft) styles.paddingLeft = `${targetPadding.paddingLeft}px`;
        if (targetPadding.paddingRight) styles.paddingRight = `${targetPadding.paddingRight}px`;
        
        return styles;
    };

    const getGizmoMargin = () => {
        const gizmoPadding = getPaddingStyles('gizmo');
        const baseMargin = 100;
        
        const rightMargin = baseMargin + (gizmoPadding.paddingRight ? parseInt(gizmoPadding.paddingRight) : 0);
        const bottomMargin = baseMargin + (gizmoPadding.paddingBottom ? parseInt(gizmoPadding.paddingBottom) : 0);
        
        return [rightMargin, bottomMargin];
    };

    useEffect(() => {
        if (props.metadata) {
            let metadata = props.metadata["metadata"];
            if (metadata["low"]) {
                setModelMetadata(metadata["low"])
            }
            else if (metadata["high"]) {
                setModelMetadata(metadata["high"])
            }
        }
    }, [props.metadata]);

    useEffect(() => {
        if (props.groupProducts) {
            setConfigurationOptions(props.groupProducts);
        }
    }, [props.groupProducts]);

    // Set initial camera position when distance changes
    useEffect(() => {
        if (distance > 0 && orbitControlsRef.current && !originalDistance) {
            const camera = orbitControlsRef.current.object;
            camera.position.set(0, 0, distance);
            orbitControlsRef.current.update();
        }
    }, [distance, originalDistance]);

    useEffect(() => {
        if (!loaded) return;
    
        const canvas = document.querySelector('.canvas-container canvas');
        if (!canvas) return;
        canvas.tabIndex = -1;
    
        let isHolding = false;

        const hideInteractionHint = () => {
            setInteractionHintEnabled(false);
            setShowInteractionHint(false);
        };

        const handlePointerDown = () => {
            hideInteractionHint();
            isHolding = true;
        };

        const handlePointerUp = () => {
            if (!isHolding) return;
            isHolding = false;
            announceView();
        };

        canvas.addEventListener('pointerdown', handlePointerDown);
        canvas.addEventListener('pointerup', handlePointerUp);
        canvas.addEventListener('pointercancel', handlePointerUp);

        return () => {
            canvas.removeEventListener('pointerdown', handlePointerDown);
            canvas.removeEventListener('pointerup', handlePointerUp);
            canvas.removeEventListener('pointercancel', handlePointerUp);
        };
    }, [loaded, announceView]);
       
    useEffect(() => {
        if (configuratorMode) {
            setInteractionHintEnabled(false);
        }
    }, [configuratorMode])

    const ScreenshotHandler = ({ loaded }) => {
        const { gl, scene, camera } = useThree();

        useEffect(() => {
            const handleMessage = (event) => {
                if (!loaded || !event.data?.getBlob || !camera || !scene || !gl) return;

                if (gridRef.current) gridRef.current.visible = false;

                // Force render
                gl.render(scene, camera);

                const imgData = gl.domElement.toDataURL('image/png');

                if (gridRef.current) gridRef.current.visible = true;

                // Post image blob back
                event.source?.postMessage({
                    blob_data: {
                        id: event.data.getBlob,
                        blob: imgData,
                    },
                }, event.origin);
            };

            window.addEventListener('message', handleMessage);
            return () => window.removeEventListener('message', handleMessage);
        }, [gl, camera, scene, loaded]);

        return null;
    };

    const modelSizeSet = (size) => {
        const width = size.x;
        const height = size.y;
        const depth = size.z;
      
        setMaxSide(Math.max(width, height, depth));
        setModelSize(width);
        setModelDimensionZ(depth);
      
        const aspect = window.innerWidth / window.innerHeight;
      
        const vFov = 20 * (Math.PI / 180); // vertical FOV
        const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      
        // Compute distances needed to fit both width and height
        const distanceForWidth = (width / 2) / Math.tan(hFov / 2);
        const distanceForHeight = (height / 2) / Math.tan(vFov / 2);
      
        // Add depth factor (for round or deep objects)
        const depthOffset = depth / 2;
      
        // Pick the larger required distance and add margin
        const distance = Math.max(distanceForWidth, distanceForHeight) + depthOffset;
      
        setDistance(distance * 1.35);
        setEnableGrid(props.debugMode);
      };

    const onDebugWireframe = (e) => {
        setWireframeMode(e.target.checked);
    };

    const onWireframeOpacityChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setWireframeOpacity(value);
    };

    const onWireframeThicknessChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setWireframeThickness(value);
    };

    const onDebugUV = (e) => {
        const isUVMode = e.target.checked;
        setUVMode(isUVMode);
        setUVSliderVisibility(isUVMode);
        
        if (isUVMode) {
            // Get the current actual camera distance from OrbitControls
            if (orbitControlsRef.current && originalDistance === null) {
                const currentDistance = orbitControlsRef.current.getDistance();
                setOriginalDistance(currentDistance);
                
                // Calculate new target distance
                const uvZoomOutFactor = 1.3;
                const targetDistance = currentDistance * uvZoomOutFactor;
                
                // Smoothly animate to new distance by manipulating camera position
                const camera = orbitControlsRef.current.object;
                const target = orbitControlsRef.current.target;
                
                // Get direction from target to camera
                const direction = camera.position.clone().sub(target).normalize();
                
                // Set new camera position at target distance
                const newPosition = target.clone().add(direction.multiplyScalar(targetDistance));
                camera.position.copy(newPosition);
                
                // Update the controls
                orbitControlsRef.current.update();
            }
        } else {
            // Restore original distance when UV mode is turned off
            if (originalDistance !== null && orbitControlsRef.current) {
                const camera = orbitControlsRef.current.object;
                const target = orbitControlsRef.current.target;
                
                // Get direction from target to camera
                const direction = camera.position.clone().sub(target).normalize();
                
                // Set camera position back to original distance
                const newPosition = target.clone().add(direction.multiplyScalar(originalDistance));
                camera.position.copy(newPosition);
                
                // Update the controls and reset stored distance
                orbitControlsRef.current.update();
                setOriginalDistance(null);
            }
        }
    };

    const onDebugPatterns = (e) => {
        setPatternsMode(e.target.checked);
    };

    const onTabChange = (e) => {
        if (e == '2') {
            setConfiguratorMode(true);
        }
        else {
            setConfiguratorMode(false);
        }
    };

    const onUVScaleChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setUVScale(value);
    };

    const onXRepeatChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setXRepeat(value);
        if (repeatLock) {
            setYRepeat(value);
        }
    };

    const onYRepeatChange = (value) => {
        if (isNaN(value)) {
            return;
        }
        setYRepeat(value);
        if (repeatLock) {
            setXRepeat(value);
        }
    };

    const onChangePattern = (value) => {
        setSelectedPattern(value);
    };

    const onChangePatternRotation = () => {
        let currentRotation = selectedPatternRotation;
        setSelectedPatternRotation(currentRotation + Math.PI/2);
    };

    const onChangeMaterialNode = (value) => {
        setSelectedMaterialNode(value);
    };

    const onChangeRepeatLock = (e) => {
        const repeatLockVal = e.target.checked;
        setRepeatLock(repeatLockVal);
        if (repeatLockVal) {
            setYRepeat(xRepeat);
        }
    };

    const onSetMaterialNodes = (materialNodes) => {
        const matNodes = [...materialNodes]
        setMaterialNodes(matNodes);
        if (matNodes && matNodes.length > 0) {
            let selectMat = matNodes[0];
            for (let mat of matNodes) {
                if (mat && typeof mat === 'string' && mat.toLowerCase().includes("fabric")){
                    selectMat = mat;
                    break;
                }
            }
            if (selectMat) {
                onChangeMaterialNode(selectMat);
            }
        }
    }

    const onBackgroundColorChange = (backgroundColor) => {
        setBackgroundColor(backgroundColor);
    }

    const onAutoRotate = (e) => {
        setAutoRotate(e.target.checked);
    };

    const handleOnModelLoaded = (data) => {
        setTreeData(data);
        setLoaded(true);
        if (props.onLoadingStatus) {
            props.onLoadingStatus('ready');
        }
        setTimeout(() => announceView('3D model. Use mouse, click or keyboard to interact with model. '), 100);
    };

    const handleOnModelError = (errorMessage) => {
        setLoaded(false);
        if (props.onLoadingStatus) {
            props.onLoadingStatus('error', errorMessage);
        }
    };

    const onConfiguratorSelect = (model) => {
        sendMessageToConfigurator('loadVariation=model:' + model);
    }

    const sendMessageToConfigurator = (message) => {
        const iframe = document.getElementById('configurator-iframe').contentWindow;
        iframe.postMessage(message, '*');
    };

    const handleVisibiltyChanged = (_visibleNodes) => {
        setVisibleNodes(_visibleNodes);
    };

    const setModifiers = (mods) => {
        let modifiersString = "";

        if (mods == undefined || mods.length == 0) {
            _setModifiers(undefined);
        }
        else {
            for (let i = 0; i < mods.length; i++) {

                modifiersString += mods[i];

                if (i < mods.length - 1) {
                    modifiersString += " ,";
                }
            }
            _setModifiers(modifiersString);
        }
    };

    const onSelectionChanged = (selection) => {
        if (selection && modelMetadata) {
            setSelectedNode(selection);
            let selectionMetadata = modelMetadata[selection];
            if (selectionMetadata) {
                setNodeClass(selectionMetadata.class);
                setVertexCount(selectionMetadata.vertex_count);
                setPolygonCount(selectionMetadata.polygon_count);
                setQuadsCount(selectionMetadata.quads);
                setTriCount(selectionMetadata.tri);
                setNgonsCount(selectionMetadata.ngons);
                setMaterialName(selectionMetadata.material_name);
                setMaterialClass(selectionMetadata.material_class);
                setModifiers(selectionMetadata.modifiers);
                setTexelDensity(selectionMetadata.texel_density);
            }
            else {
                setSelectedNode(undefined);
            }
        }
        else {
            setSelectedNode(undefined);
        }
    }

    const renderModel = () => {
        const commonProps = {
            uvChecker: uvMode,
            wireframe: wireframeMode,
            wireframeOpacity: wireframeOpacity,
            wireframeThickness: wireframeThickness,
            uvScale: uvScale,
            patternsMode: patternsMode,
            selectedPattern: selectedPattern,
            selectedPatternRotation: selectedPatternRotation,
            selectedMaterialNode: selectedMaterialNode,
            xRepeat: xRepeat,
            yRepeat: yRepeat,
            onSetMaterialNodes: onSetMaterialNodes,
            patternOptions: patternOptions,
            backgroundColor: backgroundColor,
            setModelSize: modelSizeSet,
            metadata: modelMetadata,
            onModelLoaded: handleOnModelLoaded,
            onModelError: handleOnModelError,
            visibleNodes: visibleNodes,
            selectedNode: selectedNode,
            onLoadingStatus: props.onLoadingStatus
        };

        const modelType = props.modelType || 'glb';

        if (modelType === 'unified') {
            return <UnifiedModel {...commonProps} url={props.modelUrl} />;
        } else {
            return <Model {...commonProps} url={props.modelUrl} />;
        }
    };

    const announceTimer = useRef(null);
    const scheduleAnnounce = useCallback(() => {
        if (announceTimer.current) clearTimeout(announceTimer.current);
        announceTimer.current = setTimeout(() => announceView(), 500);
    }, [announceView]);

    const handleKeyDown = useCallback((e) => {
        const controls = orbitControlsRef.current;
        if (!controls) return;
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

        const orbitStep = 0.05;
        const panStep = maxSide * 0.05;
        const zoomFactor = 1.1;

        if (e.shiftKey) {
            switch (e.key) {
                case 'ArrowLeft':  controls.target.x -= panStep; break;
                case 'ArrowRight': controls.target.x += panStep; break;
                case 'ArrowUp':    controls.target.y += panStep; break;
                case 'ArrowDown':  controls.target.y -= panStep; break;
                default: return;
            }
            controls.update();
            e.preventDefault();
        } else if (e.ctrlKey || e.metaKey) {
            switch (e.key) {
                case 'ArrowUp':   controls.dollyIn(zoomFactor); break;
                case 'ArrowDown': controls.dollyOut(zoomFactor); break;
                default: return;
            }
            controls.update();
            e.preventDefault();
        } else {
            switch (e.key) {
                case 'ArrowLeft':  controls.setAzimuthalAngle(controls.getAzimuthalAngle() - orbitStep); break;
                case 'ArrowRight': controls.setAzimuthalAngle(controls.getAzimuthalAngle() + orbitStep); break;
                case 'ArrowUp':    controls.setPolarAngle(Math.max(0.1, controls.getPolarAngle() - orbitStep)); break;
                case 'ArrowDown':  controls.setPolarAngle(Math.min(Math.PI - 0.1, controls.getPolarAngle() + orbitStep)); break;
                default: return;
            }
            controls.update();
            e.preventDefault();
            scheduleAnnounce();
        }
    }, [maxSide, scheduleAnnounce]);

    useEffect(() => {
        if (!isFocused) return;
        const onKeyDown = (e) => {
            if (e.key === 'Tab') {
                exitKeyboardMode();
                viewportFocusRef.current?.blur();
                return;
            }
            handleKeyDown(e);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isFocused, handleKeyDown, exitKeyboardMode]);

    return (
        <>
            <div
                className={`canvas-container ${loaded ? 'loaded' : 'loading'}${isFocused ? ' keyboard-focused' : ''}`}
            >
                <button
                    ref={viewportFocusRef}
                    type="button"
                    className="viewport-keyboard-entry"
                    aria-label={ariaInstructions}
                    onFocus={enterKeyboardMode}
                />
                <div aria-live="polite" className="sr-only" role="status">
                    {ariaAnnouncement}
                </div>
                {interactionHintEnabled && showInteractionHint && loaded && !isFocused && (
                    <div className="interaction-hint">
                            <img src="./img/pointer-icon.svg" alt="Interaction Hint" className="interaction-hint-icon" />
                    </div>
                )}
                {configuratorMode ? <div id="configurator-container">
                    <iframe
                        id="configurator-iframe"
                        src={CONFIGURATOR_INSPECTOR}
                        frameBorder="0"
                        title="3D Configurator"
                        onLoad={() => {
                            sendMessageToConfigurator('changeMode=custom');
                            onConfiguratorSelect(props.modelUrl);
                        }}
                    />
                </div>
                :
                <Canvas
                    gl={{ toneMapping: glToneMapping, toneMappingExposure: 1 }}
                    flat
                    shadows>
                    <color attach="background" args={[backgroundColor]} />
                    <PerspectiveCamera
                        fov={20}
                        position={[0, 0, distance]}
                        near={0.01}
                        makeDefault
                    />

                 
                    <InteractionHintAnimator 
                        showInteractionHint={interactionHintEnabled && showInteractionHint } 
                        orbitControlsRef={orbitControlsRef} 
                        setShowInteractionHint={setShowInteractionHint}
                        allowReshow={interactionHintEnabled}
                    />

                    <Suspense fallback={null}>
                        <EnvironmentHandler environmentKey={environmentHDR} options={hdrEnvironmentOptions} />
                        {isDirectionalLightHdr[environmentHDR] && <directionalLight 
                            position={[0, -maxSide * 8, 0]} 
                            intensity={1.6} 
                            color="#ffffff"
                            castShadow={true}
                            shadow-mapSize-width={2048}
                            shadow-mapSize-height={2048}
                        />}
                        <Center>
                            {renderModel()}
                            {uvMode && <WorldScaleRef modelSize={modelSize} modelRef={refModel} />}
                            {enableGrid ? <Grid ref={gridRef} infiniteGrid fadeDistance={7.0} cellSize={0.05} /> : ''}
                        </Center>
                        {uvMode && <UVCheckerPlane modelSize={modelSize} modelDimensionZ={modelDimensionZ} orbitControlsRef={orbitControlsRef} />}
                        {
                            !hideGizmo && (
                                <GizmoHelper 
                                    alignment="bottom-right" 
                                    margin={getGizmoMargin()}
                                >
                                    <GizmoViewport labelColor="white" axisHeadScale={1} />
                                </GizmoHelper>
                            )
                        }
                    <ScreenshotHandler loaded={loaded} />
                    <OrbitControls
                        makeDefault
                        autoRotate={autoRotate}
                        minDistance={maxSide * 1.2}
                        maxDistance={Math.max(maxSide * 8, distance * 2)}
                        autoRotateSpeed={autoRotationSpeed}
                        enablePan={true}
                        enableDamping={true} 
                        ref={orbitControlsRef}
                    />
                    </Suspense>
                </Canvas>}
                {isFocused && loaded && !isIOS && !isAndroid && (
                    <div className="keyboard-hints-container" role="group" aria-label="Keyboard controls help">
                        {hintsOpen && (
                            <div
                                id="keyboard-hints-panel"
                                className="keyboard-hints-panel"
                                role="region"
                                aria-label="Keyboard shortcuts for 3D viewer"
                            >
                                <p className="keyboard-hints-title" aria-hidden="true">Keyboard Controls</p>
                                <ul className="keyboard-hints-list" aria-label="List of keyboard shortcuts">
                                    <li aria-label="Arrow keys Up Down Left Right: Orbit the camera">
                                        <span className="keyboard-hints-keys" aria-hidden="true">
                                            <kbd>↑</kbd><kbd>↓</kbd><kbd>←</kbd><kbd>→</kbd>
                                        </span>
                                        <span className="keyboard-hints-action" aria-hidden="true">Orbit</span>
                                    </li>
                                    <li aria-label="Shift plus Arrow keys: Pan the camera">
                                        <span className="keyboard-hints-keys" aria-hidden="true">
                                            <kbd>Shift</kbd> + <kbd>↑↓  ←→</kbd>
                                        </span>
                                        <span className="keyboard-hints-action" aria-hidden="true">Pan</span>
                                    </li>
                                    <li aria-label="Control plus Arrow Up or Down: Zoom in or out">
                                        <span className="keyboard-hints-keys" aria-hidden="true">
                                            <kbd>Ctrl</kbd> + <kbd>↑↓</kbd>
                                        </span>
                                        <span className="keyboard-hints-action" aria-hidden="true">Zoom</span>
                                    </li>
                                </ul>
                            </div>
                        )}
                        <button
                            className={`keyboard-hints-toggle${hintsOpen ? ' active' : ''}`}
                            aria-label={hintsOpen ? "Hide keyboard shortcuts panel" : "Show keyboard shortcuts panel"}
                            aria-expanded={hintsOpen}
                            aria-controls="keyboard-hints-panel"
                            onClick={(e) => { e.stopPropagation(); setHintsOpen(h => !h); }}
                        >
                            <span aria-hidden="true">?</span>
                        </button>
                    </div>
                )}
            </div>
            {!loaded && (
                <div className="loading-overlay">
                    <LoadStatus loadingText={"Loading Model..."} />
                </div>
            )}
            {props.debugMode ? 
                <div className="inspectorPanel" style={getPaddingStyles('debugPanel')}>
                    <Collapse>
                        <Collapse.Panel style={{ fontSize: "small" , backgroundColor:"#ffffff" }} header="Debug Modes" key="1">
                            <DebugPanel
                                selectedProducts={props.selectedProducts}
                                onAutoRotate={onAutoRotate}
                                onDebugWireframe={onDebugWireframe}
                                wireframeMode={wireframeMode}
                                wireframeOpacity={wireframeOpacity}
                                onWireframeOpacityChange={onWireframeOpacityChange}
                                wireframeThickness={wireframeThickness}
                                onWireframeThicknessChange={onWireframeThicknessChange}
                                onDebugUV={onDebugUV}
                                onDebugPatterns={onDebugPatterns}
                                onConfiguratorSelect={onConfiguratorSelect}
                                showConfiguratorMode={props.configuratorMode}
                                configuratorOptions={configuratorOptions}
                                onTabChange={onTabChange}
                                onUVScaleChange={onUVScaleChange}
                                onXRepeatChange={onXRepeatChange}
                                onYRepeatChange={onYRepeatChange}
                                onChangePattern={onChangePattern}
                                onChangePatternRotation={onChangePatternRotation}
                                onChangeMaterialNode={onChangeMaterialNode}
                                selectedPattern={selectedPattern}
                                selectedMaterialNode={selectedMaterialNode}
                                materialNodes={materialNodes}
                                backgroundColor={backgroundColor}
                                onBackgroundColorChange={onBackgroundColorChange}
                                uvSliderVisibility={uvSliderVisibility}
                                patternsMode={patternsMode}
                                uvScale={uvScale}
                                xRepeat={xRepeat}
                                yRepeat={yRepeat}
                                repeatLock={repeatLock}
                                onChangeRepeatLock={onChangeRepeatLock}
                                patternOptions={patternOptions}
                                setToneMapping={setToneMapping}
                                toneMapping={toneMapping}
                                setGLToneMapping={setGLToneMapping}
                                toneMappingOptions={toneMappingOptions}
                                environmentHDR={environmentHDR}
                                setEnvironmentHDR={setEnvironmentHDR}
                                hdrEnvironmentOptions={hdrEnvironmentOptions}
                            />
                        </Collapse.Panel>
                        <Collapse.Panel style={{ fontSize: "small", backgroundColor:"#ffffff" }} header="Hierarchy" key="2">
                            {treeData != null && <Outliner
                                data={treeData}
                                onVisibilityChanged={handleVisibiltyChanged}
                                onSelectionChanged={onSelectionChanged} />}
                        </Collapse.Panel>
                    </Collapse>

                </div>
            : ''}
        </>
    );
};
export default Model_Inspector;